import { useCallback, useMemo, useState } from 'react';

import { Box, Button, CircularProgress, Stack, Tooltip } from '@mui/material';
import QRCodeStyling from 'qr-code-styling';

import { useAppSelector } from 'hooks/store/useAppStore';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { PageMapConfig } from 'types/builder/pageConfig';

import { ChangesResetModal } from '../ChangesResetModal';

type PageSaverControlProps = {
    config?: PageMapConfig;
    hasChanges: boolean;
    loading: boolean;
    onSave: () => void;
    onReset: () => void;
};

export const PageSaverControl = ({
    config,
    hasChanges,
    loading,
    onReset,
    onSave,
}: PageSaverControlProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);

    const { errors } = useAppSelector((state) => state.pageBuilder);

    const error = useMemo(
        () =>
            errors.includes('pageSettings') || errors.includes('quizSettings'),
        [errors]
    );

    const locationUrl = useMemo(
        () =>
            config?.uuid
                ? `${window.location.origin}/?page=${config?.uuid}`
                : undefined,
        [config?.uuid]
    );

    const [copied, copy] = useCopyToClipboard();

    const onQrGenerate = useCallback(() => {
        const qrCode = new QRCodeStyling({
            width: 300,
            height: 300,
            type: 'svg',
            data: locationUrl,
            // image: logo,
            dotsOptions: {
                type: 'rounded',
            },
            imageOptions: {
                crossOrigin: 'anonymous',
                margin: 10,
                imageSize: 0.6,
            },
        });

        qrCode.download({
            name: `QR-код для «${config?.pageTitle}»`,
            extension: 'svg',
        });
    }, [config?.pageTitle, locationUrl]);

    return (
        <Stack
            spacing={2}
            sx={{
                p: '20px 28px',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Stack direction="row" spacing={2}>
                <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    color="error"
                    disabled={!hasChanges || loading}
                    onClick={() => setIsOpen(true)}
                >
                    Отменить изменения
                </Button>

                <Tooltip
                    title={error ? 'Исправьте ошибки перед сохранением' : ''}
                    arrow
                    placement="bottom"
                >
                    <Box sx={{ width: '100%' }}>
                        <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            disabled={!hasChanges || loading || error}
                            onClick={() => onSave()}
                            sx={{ height: '100%' }}
                        >
                            {loading ? (
                                <CircularProgress
                                    size={20}
                                    variant="indeterminate"
                                    color="inherit"
                                />
                            ) : (
                                'Сохранить страницу'
                            )}
                        </Button>
                    </Box>
                </Tooltip>
            </Stack>
            {config ? (
                <Stack direction="row" spacing={2}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => copy(locationUrl || '')}
                        disabled={!!copied}
                        fullWidth
                    >
                        {!copied ? 'Ссылка на страницу' : 'Скопировано'}
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => onQrGenerate()}
                        fullWidth
                    >
                        Скачать QR-код
                    </Button>
                </Stack>
            ) : undefined}
            <ChangesResetModal
                onClose={() => setIsOpen(false)}
                open={isOpen}
                onConfirm={() => onReset()}
            />
        </Stack>
    );
};
